import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';

import RatingEmoji from 'components/RatingEmoji';
import { currentLanguage } from 'helpers/utils';
import usePublicApi from 'public-api';
import { getErrors, isResponse } from 'public-api/utils';
import { useAsyncFunction } from 'stores/hooks';
import { Button, Card, Form, Icon, Loader, Message, Segment } from 'ui';

import { usePublicCompany } from './hooks';

type RatingOptions = {
  text: string;
  value: 0 | 1 | 2;
};

function TicketRate() {
  const api = usePublicApi();
  const { ratingCommentId } = useParams<{
    ratingCommentId: UUIDField;
  }>() as { ratingCommentId: UUIDField };

  const { company } = usePublicCompany();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [ticketRating, setTicketRating] = useState<BackendPublic.TicketRating | null>(null);

  const send = async () => {
    try {
      const data = await api.ClientRatingCommentApi.patch({
        id: ratingCommentId,
        request: {
          rating: ticketRating?.rating,
          rating_comment: ticketRating?.rating_comment,
          permission_to_publish: ticketRating?.permission_to_publish,
        },
      });
      setTicketRating(data);
      setSuccess(true);
    } catch (error) {
      if (isResponse(error)) {
        const responseText = await getErrors(error);
        setError(responseText);
      }
    }
  };

  const load = async () => {
    try {
      const data = await api.ClientRatingCommentApi.retrieve({
        id: ratingCommentId,
      });
      setTicketRating(data);

      if (data.rating_comment_response || data.rating_comment) {
        setSuccess(true);
      }
    } catch (error) {
      if (isResponse(error)) {
        const responseText = await getErrors(error);
        setError(responseText);
      }
    }
  };

  const [handleSend, isSending] = useAsyncFunction(send);
  const [handleLoad, isLoading] = useAsyncFunction(load);

  useEffect(() => {
    handleLoad();
  }, []);

  if (company === null) {
    return null;
  }

  if (isLoading || !ticketRating) {
    return (
      <Segment padded>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <Loader />
          </div>
        </div>
      </Segment>
    );
  }

  const ratingOptions: RatingOptions[] = [
    {
      text: gettext('Perfect'),
      value: 2,
    },
    {
      text: gettext('Good, but...'),
      value: 1,
    },
    {
      text: gettext('Bad'),
      value: 0,
    },
  ];

  return success ? (
    <Segment padded>
      <div className="space-y-4">
        <h3 className="text-2xl font-medium">{gettext('Thank you for your rating!')}</h3>
        <p>{gettext('We will improve our services based on your feedback.')}</p>
        <div className="pt-2">
          <Card flat>
            <div className="p-5">
              <div className="flex items-baseline justify-between">
                <div className="flex items-center mb-2">
                  <strong className="pr-2">{gettext('Your rating')}</strong>
                  <RatingEmoji rating={ticketRating?.rating as 0 | 1 | 2} size="small" />
                </div>
                <p className="text-gray-600">{moment(ticketRating?.created_at).format('LL')}</p>
              </div>
              <p>{ticketRating?.rating_comment}</p>
              {ticketRating?.rating_comment_response && (
                <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded-lg shadow">
                  <div className="flex items-center mb-2">
                    <Icon name="reply" className="text-gray-700 w-5 h-5 mr-2" />
                    <p className="font-semibold text-gray-800">{currentLanguage(company.name)}</p>
                  </div>
                  <p className="text-gray-800">{ticketRating?.rating_comment_response}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Segment>
  ) : (
    <Segment padded>
      <h3 className="text-center text-2xl font-medium">{gettext('Thank you for your rating!')}</h3>
      <div className="flex items-start justify-center gap-8 py-8">
        {ratingOptions.map((option) => (
          <div style={{ opacity: ticketRating.rating === option.value ? 1 : 0.2 }} key={option.value}>
            <button
              onClick={() =>
                setTicketRating((prev) => ({
                  ...prev!,
                  rating: option.value,
                }))
              }
              type="button"
              aria-label={`Set rating to ${option.text}`}
            >
              <RatingEmoji rating={option.value} size="massive" />
            </button>
            <div className="pt-3 text-center">{option.text}</div>
          </div>
        ))}
      </div>
      <Form>
        <Form.Fields>
          <Form.Textarea
            label={gettext('Help us improve. Tell us what you liked or disliked')}
            onChange={(value) =>
              setTicketRating((prev) => ({
                ...prev!,
                rating_comment: value,
              }))
            }
            value={ticketRating?.rating_comment}
          />
          <Form.Checkbox
            label={gettext('I agree to make this review publicly available.')}
            onChange={(value) =>
              setTicketRating((prev) => ({
                ...prev!,
                permission_to_publish: value,
              }))
            }
            value={ticketRating.permission_to_publish ?? true}
          />
          <div className="flex justify-end">
            <Button primary loading={isSending} onClick={handleSend}>
              {gettext('Send ticket rating')}
            </Button>
          </div>
          {error && <Segment color="red">{error}</Segment>}
        </Form.Fields>
      </Form>
    </Segment>
  );
}

export default observer(TicketRate);
